import { baseApi } from './baseApi';
import { mapAllUsersResponse, mapApiUserToModel, mapModelUserToApi, mapUserGroupResponse } from '../../apiEndpoints';
import type { User, UserGroup, UserWithGroupIds } from '../../../types';
import type { JsonValue } from './baseQuery';

interface JsonLinks {
    next?: {
        href?: string;
    };
}

const getNextHref = (response: JsonValue | null): string | undefined => {
    if (!response || typeof response !== 'object' || Array.isArray(response) || !('_links' in response)) {
        return undefined;
    }

    const links = response._links as JsonLinks | undefined;
    return links?.next?.href;
};

export const usersApi = baseApi.injectEndpoints({
    endpoints: build => ({
        getUsers: build.query<User[], void>({
            async queryFn(_arg, _api, _extraOptions, baseQuery) {
                const users: User[] = [];
                let nextLink: string | undefined = undefined;

                do {
                    const result = await baseQuery(
                        nextLink
                            ? {
                                  absoluteUrl: nextLink,
                              }
                            : {
                                  service: 'userAdmin',
                                  path: '/users',
                                  params: {
                                      limit: 1000,
                                  },
                              }
                    );

                    if (result.error) {
                        return { error: result.error, meta: result.meta };
                    }

                    users.push(...mapAllUsersResponse({ body: result.data }));
                    nextLink = getNextHref(result.data);
                } while (nextLink);

                return { data: users };
            },
            providesTags: result => [
                { type: 'UserList', id: 'LIST' },
                ...(result?.map(user => ({ type: 'User' as const, id: user.id })) ?? []),
            ],
        }),
        getGroups: build.query<UserGroup[], void>({
            query: () => ({
                service: 'userAdmin',
                path: '/groups',
                params: {
                    limit: 100,
                },
            }),
            transformResponse: response => mapUserGroupResponse({ body: response }),
            providesTags: result => [
                { type: 'GroupList', id: 'LIST' },
                ...(result?.map(group => ({ type: 'Group' as const, id: group.id })) ?? []),
            ],
        }),
        inviteUser: build.mutation<User, UserWithGroupIds>({
            query: user => ({
                service: 'userAdmin',
                path: `/users/${user.id}`,
                method: 'PUT',
                body: mapModelUserToApi(user),
                headers: {
                    Accept: 'application/json',
                    'If-None-Match': '*',
                },
            }),
            transformResponse: response => mapApiUserToModel(response as never),
            invalidatesTags: [{ type: 'UserList', id: 'LIST' }],
        }),
        deleteUser: build.mutation<void, User>({
            query: user => ({
                service: 'userAdmin',
                path: `/users/${user.id}`,
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
            invalidatesTags: (_result, _error, user) => [
                { type: 'User', id: user.id },
                { type: 'UserList', id: 'LIST' },
                { type: 'DriverList', id: 'LIST' },
            ],
        }),
    }),
});

export const {
    useDeleteUserMutation,
    useGetGroupsQuery,
    useGetUsersQuery,
    useInviteUserMutation,
    useLazyGetGroupsQuery,
} = usersApi;
